interface StatusThresholds {
  staleMinutes?: number;
  movingSpeedKmh?: number;
  /** Speed below which a reported `ignition: false` overrides "moving"
   *  classification — guards against GPS-jitter Doppler readings on a
   *  parked vehicle (typically 5–8 km/h despite the car not moving).
   *  Default 10 km/h. Devices with no ignition wiring (`ignition`
   *  undefined) are NOT affected — only an explicit `false` triggers it. */
  jitterSpeedKmh?: number;
  /** A vehicle whose last fix is older than this gets reclassified from
   *  `moving` to `pending` even if the global `staleMinutes` threshold
   *  hasn't fired yet. Parked vehicles legitimately stay silent for
   *  minutes between pings, but a vehicle reporting > 0 km/h going
   *  silent for several minutes is highly suspicious (tunnel, SIM drop,
   *  power cut) — keeping it as "moving" misleads operators. Default
   *  180s. Stationary classifications are unaffected. */
  movingStaleSeconds?: number;
}

interface DeviceLike {
  status?: string;
}

interface PositionLike {
  fixTime?: string;
  fix_time?: string;
  speed?: number;
  attributes?: { ignition?: boolean } | null;
}

export type VehicleStatus = 'moving' | 'idle' | 'stopped' | 'offline' | 'pending';

/** Get vehicle status from device + position, honoring optional per-org
 *  thresholds. `thresholds`: { staleMinutes, movingSpeedKmh } — when omitted,
 *  uses sensible defaults (10 min stale, >0 km/h = moving) to preserve legacy
 *  behavior for callers that haven't migrated to useStatusThresholds yet.
 *
 *  `position.speed` is stored in knots (Traccar-native); `movingKmh`
 *  threshold is in km/h. The * 1.852 conversion is load-bearing — removing it
 *  reintroduces the 2026-04-24 GPS-jitter-renders-as-moving bug.
 *
 *  Offline vs pending: `device.status` reflects the Traccar server's heartbeat
 *  view of the tracker. If the server marks it `offline`, the device is gone
 *  (no power / out of cellular). If the server still sees it as `online` but
 *  the latest `position.fixTime` is stale, the tracker is alive but its GPS
 *  can't fix — that's `pending`. Collapsing these two into one bucket loses
 *  the distinction operators need to triage. */
export function getVehicleStatus(
  device: DeviceLike | null | undefined,
  position: PositionLike | null | undefined,
  thresholds?: StatusThresholds,
): VehicleStatus {
  if (!device) return 'offline';
  const staleMs = (thresholds?.staleMinutes ?? 10) * 60_000;
  const movingStaleMs = (thresholds?.movingStaleSeconds ?? 180) * 1_000;
  const movingKmh = thresholds?.movingSpeedKmh ?? 0;
  const jitterKmh = thresholds?.jitterSpeedKmh ?? 10;
  const fixRaw = position?.fixTime || position?.fix_time;
  const fix = fixRaw ? new Date(fixRaw).getTime() : NaN;
  const isStale = !Number.isFinite(fix) || Date.now() - fix > staleMs;
  if (device.status === 'offline') return 'offline';
  if (isStale) return 'pending';
  const speedKmh = (position?.speed ?? 0) * 1.852;
  const ignition = position?.attributes?.ignition;
  // GPS-jitter guard: device explicitly reports ignition:false AND speed is
  // below the jitter ceiling → treat as stopped, not moving. Verified 2026-05-21
  // against staging device 15 (KIA-X): post-ignition-off Doppler readings of
  // 5–6 km/h with 2.5 m between fixes (= 0.3 km/h actual movement) were
  // misclassified as "moving". Devices without ignition wiring (`ignition`
  // undefined) fall through unchanged.
  const isJitter = ignition === false && speedKmh < jitterKmh;
  if (speedKmh > movingKmh && !isJitter) {
    // Stale-while-moving guard: a > 0 km/h reading from N minutes ago is
    // much less credible than a "parked" reading from N minutes ago. A
    // vehicle that was moving at 79 km/h and then went silent for 7 min
    // could be anywhere — keeping it as "moving" misleads operators. The
    // shorter `movingStaleSeconds` threshold reclassifies these to pending
    // so the marker visually signals "we don't know its current state."
    if (Number.isFinite(fix) && Date.now() - fix > movingStaleMs) return 'pending';
    return 'moving';
  }
  if (ignition) return 'idle';
  return 'stopped';
}

/**
 * Whether a position should be treated as the device sitting still and
 * the GPS chip emitting noise — explicit `ignition: false` AND speed
 * below the jitter ceiling. Used by the telemetry panel (to suppress the
 * speed / heading values), the live-map animator (to skip animating
 * tiny no-move legs), and the trail builder (to skip appending zigzag
 * vertices when a vehicle is parked). Mirrors the AND-gate inside
 * `getVehicleStatus` so all four surfaces agree.
 *
 * Devices without ignition wiring (`ignition` undefined) return false —
 * we can't safely assume jitter without the ground truth.
 */
export function isStationaryFix(
  position: PositionLike | null | undefined,
  thresholds?: Pick<StatusThresholds, 'jitterSpeedKmh'>,
): boolean {
  if (!position) return false;
  const jitterKmh = thresholds?.jitterSpeedKmh ?? 10;
  const speedKmh = (position.speed ?? 0) * 1.852;
  return position.attributes?.ignition === false && speedKmh < jitterKmh;
}
